import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import useWindowSize from '../../utils/windowSize.js';

/**
 * Unified SearchBar component with configurable parameters
 * 
 * @param {Function} onSearch - Callback function called when search value changes (for uncontrolled mode)
 * @param {string} value - Controlled value (if provided, component is controlled)
 * @param {Function} onChange - Controlled onChange handler (required if value is provided)
 * @param {string} placeholder - Placeholder text (default: "Search...")
 * @param {string} label - Label text (if provided, will use label instead of placeholder)
 * @param {string|number} width - Width of the search field. Can be percentage string (e.g., "50%") or number in px (default: "50%")
 * @param {string} justifyContent - Flexbox justify-content for container (default: "center")
 * @param {string|number} marginTop - Top margin (default: "10px")
 * @param {string|number} marginBottom - Bottom margin (default: "10px")
 * @param {string|number} marginLeft - Left margin (default: undefined)
 * @param {string|number} marginRight - Right margin (default: undefined)
 * @param {string|number} margin - All margins (overrides marginTop/marginBottom if provided)
 * @param {boolean} fullWidth - Whether TextField should be fullWidth (default: false)
 * @param {string} size - TextField size: "small" | "medium" (default: "small")
 */
function SearchBar({ 
    onSearch,
    value: controlledValue,
    onChange: controlledOnChange,
    placeholder = "Search...",
    label,
    width = "50%",
    justifyContent = "center",
    marginTop = "10px",
    marginBottom = "10px",
    marginLeft,
    marginRight,
    margin,
    fullWidth = false,
    size = "small"
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const { width: windowWidth } = useWindowSize();
    const isSmallScreen = windowWidth < 900;

    // Determine if component is controlled or uncontrolled
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : searchTerm;
    
    // Calculate responsive width for small screens
    const getResponsiveWidth = () => {
        if (isSmallScreen) {
            // On small screens, use more width
            if (typeof width === 'string' && width.includes('%')) {
                const percent = parseInt(width);
                return `${Math.min(percent + 20, 90)}%`;
            }
            return '90%';
        }
        return width;
    };

    const handleChange = (e) => {
        const newValue = e.target.value;
        
        if (isControlled) {
            // Controlled mode: call onChange if provided
            if (controlledOnChange) {
                controlledOnChange(e);
            }
        } else {
            // Uncontrolled mode: update local state and call onSearch
            setSearchTerm(newValue);
            if (onSearch) {
                onSearch(newValue);
            }
        }
    };

    const containerMargin = margin 
        ? { margin } 
        : { 
            marginTop, 
            marginBottom,
            ...(marginLeft !== undefined && { marginLeft }),
            ...(marginRight !== undefined && { marginRight })
        };

    return (
        <Box sx={{ 
            ...containerMargin, 
            display: "flex", 
            justifyContent: justifyContent, 
            minWidth: marginLeft === "0px" && marginRight === "0px" ? 'auto' : (isSmallScreen ? '100%' : '65%')
        }}>
            <TextField
                value={value}
                onChange={handleChange}
                placeholder={!label ? placeholder : undefined}
                label={label}
                variant="outlined"
                size={size}
                fullWidth={fullWidth}
                sx={{
                    width: fullWidth ? "100%" : getResponsiveWidth(),
                    backgroundColor: "#f0f0f0",
                    borderRadius: '5px',
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                            borderColor: "#ccc",
                        },
                        "&:hover fieldset": {
                            borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#333",
                        },
                    },
                    "& .MuiInputBase-input": {
                        color: "#333",
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#666" }} />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
}

export default SearchBar;
